import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * CARD PLEGABLE CON MEMORIA.
 *
 * El tab de Payments del claim apilaba sus secciones siempre abiertas: 6.570px de scroll (más de
 * siete pantallas) en la pantalla que más se usa en el día. Buena parte de ese alto no es
 * información para leer sino ACCIONES que se abren cuando se van a hacer (generar el invoice,
 * autorizar un pago, soltar el PDF del estimado), así que ocupaban pantalla sin decir nada.
 *
 * Acá se pliegan, con dos reglas para que plegar no esconda trabajo:
 *  - el `hint` del encabezado sigue reportando el estado con la sección CERRADA (si ya está
 *    autorizado, de qué banco es la cuenta), así que nunca desaparece un dato de la vista;
 *  - lo que el usuario abre o cierra a mano queda guardado por sección (localStorage): cada quien
 *    acomoda su pantalla una vez y se queda así. Mientras no la toque manda `defaultOpen`, que
 *    puede llegar tarde (cuando carga el dato) — por eso se re-aplica si cambia.
 *
 * `anchorId` es para las secciones con deep-link (#sec-payments): si el link apunta a esta
 * sección se abre sola aunque esté plegada por defecto.
 */
/**
 * Suscribe un plegable HECHO A MANO (los que ya tenían su propio estado, como la lista de
 * estimados) al "Plegar todo / Abrir todo", sin obligarlos a convertirse en FoldCard.
 */
export function useFoldAll(aplicar: (open: boolean) => void) {
  const ref = useRef(aplicar);
  ref.current = aplicar;
  useEffect(() => {
    const h = (e: Event) => ref.current((e as CustomEvent<{ open: boolean }>).detail.open);
    window.addEventListener('fold:all', h);
    return () => window.removeEventListener('fold:all', h);
  }, []);
}

/**
 * Estado abierto/cerrado con la MISMA memoria y el mismo "Plegar todo" que FoldCard, para los
 * plegables que dibujan su propio encabezado (la lista de estimados). Sin esto, "Plegar todo"
 * los cerraba pero al recargar volvían abiertos: media pantalla que reaparecía sola.
 */
export function useFoldState(id: string, defaultOpen: boolean): [boolean, (v: boolean) => void] {
  const key = `fold:${id}`;
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(key); return v === null ? defaultOpen : v === '1'; }
    catch { return defaultOpen; }
  });
  const set = useCallback((v: boolean) => {
    setOpen(v);
    try { localStorage.setItem(key, v ? '1' : '0'); } catch { /* modo privado */ }
  }, [key]);
  useFoldAll(set);
  return [open, set];
}

/** Pliega o abre TODAS las secciones plegables que estén montadas. */
export function foldAll(open: boolean) {
  window.dispatchEvent(new CustomEvent('fold:all', { detail: { open } }));
  /* Los <details className="card fold"> escritos a mano (updates del depto, email, grabaciones)
     no tienen estado de React: se abren/cierran por DOM. Los de FoldCard se saltan — ahí manda
     el estado, tocarlos por fuera lo dejaría desincronizado. */
  document.querySelectorAll<HTMLDetailsElement>('details.fold:not([data-fold-react])')
    .forEach((d) => { d.open = open; });
}

export function FoldCard({ id, title, hint, defaultOpen = true, anchorId, className, children }: {
  id: string;
  title: ReactNode;
  hint?: ReactNode;
  defaultOpen?: boolean;
  anchorId?: string;
  className?: string;
  children: ReactNode;
}) {
  const key = `fold:${id}`;
  const guardado = () => { try { return localStorage.getItem(key); } catch { return null; } };
  /** Ya hay preferencia del usuario para esta sección: `defaultOpen` deja de mandar. */
  const tocado = useRef(guardado() !== null);
  const [open, setOpen] = useState(() => {
    if (anchorId && typeof window !== 'undefined' && window.location.hash === `#${anchorId}`) return true;
    const v = guardado();
    return v === null ? defaultOpen : v === '1';
  });

  useEffect(() => { if (!tocado.current) setOpen(defaultOpen); }, [defaultOpen]);

  /* "Plegar todo / Abrir todo" de la pestaña: un evento en vez de pasar estado por props a
     secciones que viven en componentes distintos. Cuenta como decisión del usuario, así que se
     guarda igual que un clic en el encabezado. */
  useEffect(() => {
    const h = (e: Event) => {
      const abierto = (e as CustomEvent<{ open: boolean }>).detail.open;
      tocado.current = true;
      setOpen(abierto);
      try { localStorage.setItem(key, abierto ? '1' : '0'); } catch { /* modo privado */ }
    };
    window.addEventListener('fold:all', h);
    return () => window.removeEventListener('fold:all', h);
  }, [key]);

  return (
    <details
      id={anchorId}
      className={`card fold${className ? ` ${className}` : ''}`}
      data-fold-react="1"
      open={open}
      onToggle={(e) => {
        const abierto = e.currentTarget.open;
        setOpen(abierto);
        // Solo se persiste lo que abrió/cerró una PERSONA, no el default que llega con el dato.
        if (tocado.current) { try { localStorage.setItem(key, abierto ? '1' : '0'); } catch { /* modo privado */ } }
      }}
    >
      <summary className="fold-sum" onClick={() => { tocado.current = true; }}>
        <h2 className="card-title" style={{ margin: 0 }}>{title}</h2>
        {hint != null && hint !== '' && <span className="fold-hint">{hint}</span>}
      </summary>
      <div className="fold-body">{children}</div>
    </details>
  );
}
